import type { PaddleCheckoutPolicy } from '@segmently/schema-models/payments';

/** Validate persisted settings before generated JSON converters can discard unknown keys.
 * No SDK DOM target, arbitrary URL or merchant approval can be supplied by this object.
 * Undefined preserves the legacy per-product mapping; an invalid explicit value never does.
 */
export function readPaddleCheckoutPolicy(value: unknown): PaddleCheckoutPolicy | undefined {
  if (value === undefined) return undefined;
  const invalid = (): never => { throw new Error('Invalid Paddle checkout policy.'); };
  if (!value || typeof value !== 'object' || Array.isArray(value)) return invalid();
  const row = value as Record<string, unknown>;
  if (Object.keys(row).some(key => !['schemaVersion', 'surface', 'navigation', 'returnPolicyId'].includes(key)) ||
    row.schemaVersion !== 1 || !['overlay', 'inline', 'express'].includes(row.surface as string) ||
    !['in_page', 'merchant_redirect'].includes(row.navigation as string)) return invalid();
  if (row.navigation === 'in_page' && Object.prototype.hasOwnProperty.call(row, 'returnPolicyId')) return invalid();
  if (row.navigation === 'merchant_redirect' &&
    (typeof row.returnPolicyId !== 'string' || !/^[A-Za-z0-9_.-]{1,128}$/.test(row.returnPolicyId))) return invalid();
  return { schemaVersion: 1, surface: row.surface as PaddleCheckoutPolicy['surface'],
    navigation: row.navigation as PaddleCheckoutPolicy['navigation'],
    ...(row.navigation === 'merchant_redirect' ? { returnPolicyId: row.returnPolicyId as string } : {}) };
}

/** Public SDK settings frozen with a merchant offer; never an API or webhook secret. */
export interface PaddleMerchantCheckout {
  surface: 'overlay' | 'inline' | 'express';
  clientToken: string;
}
export function readPaddleMerchantCheckout(value: unknown, environment: 'sandbox' | 'production'): Readonly<PaddleMerchantCheckout> {
  const invalid = (): never => { throw new Error('Invalid Paddle merchant checkout.'); };
  if (!value || typeof value !== 'object' || Array.isArray(value) || Object.keys(value).length !== 2 ||
    Object.keys(value).some(key => !['surface', 'clientToken'].includes(key))) return invalid();
  const row = value as PaddleMerchantCheckout;
  const prefix = environment === 'sandbox' ? 'test_' : environment === 'production' ? 'live_' : '';
  if (!prefix || !['overlay', 'inline', 'express'].includes(row.surface) ||
    typeof row.clientToken !== 'string' || row.clientToken.length > 1024 ||
    !new RegExp(`^${prefix}\\S+$`).test(row.clientToken)) return invalid();
  return Object.freeze({ surface: row.surface, clientToken: row.clientToken });
}
